"use client";

import { useActionState } from "react";
import { useFormStatus } from "react-dom";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import { contactAction } from "@/app/actions/leads";

const FIELDS = [
  { name: "name", label: "Name", placeholder: "Your name" },
  { name: "email", label: "Email", placeholder: "you@company.com", type: "email" },
  { name: "subject", label: "Subject", placeholder: "What's this about?" },
] as const;

function SubmitButton() {
  const { pending } = useFormStatus();
  return (
    <button
      type="submit"
      disabled={pending}
      className="group w-full flex items-center justify-center gap-2 py-4 bg-cl-green text-cl-bg font-display font-bold text-sm rounded hover:bg-cl-green/90 transition-colors disabled:opacity-40"
    >
      {pending ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <>
          Send Message{" "}
          <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
        </>
      )}
    </button>
  );
}

export function ContactForm() {
  const [state, formAction] = useActionState(contactAction, undefined);

  if (state?.ok) {
    return (
      <div className="min-h-screen bg-cl-bg flex items-center justify-center text-center px-4">
        <div>
          <Check className="w-12 h-12 text-cl-green mx-auto mb-4" />
          <h1 className="font-display font-bold text-3xl text-cl-text mb-3">
            Message Sent
          </h1>
          <p className="text-cl-muted text-sm">
            We'll get back to you within 24 hours.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cl-bg">
      <div className="max-w-xl mx-auto px-4 lg:px-8 pt-32 pb-24">
        <p className="text-[10px] font-mono text-cl-muted uppercase tracking-widest mb-4">
          Get in Touch
        </p>
        <h1 className="font-display font-bold text-3xl text-cl-text mb-10">
          Contact
        </h1>
        <form action={formAction} className="space-y-8">
          {FIELDS.map((field) => (
            <div key={field.name}>
              <label className="block text-[10px] font-mono text-cl-muted uppercase tracking-widest mb-2">
                {field.label}
              </label>
              <input
                type={"type" in field ? field.type : "text"}
                name={field.name}
                placeholder={field.placeholder}
                required={field.name !== "subject"}
                className="w-full bg-transparent border-0 border-b border-cl-border pb-2 text-cl-text text-base placeholder:text-cl-muted/20 focus:border-cl-green focus:outline-none transition-colors"
              />
            </div>
          ))}
          <div>
            <label className="block text-[10px] font-mono text-cl-muted uppercase tracking-widest mb-2">
              Message
            </label>
            <textarea
              name="message"
              required
              rows={4}
              placeholder="Your message..."
              className="w-full bg-transparent border-0 border-b border-cl-border pb-2 text-cl-text text-base placeholder:text-cl-muted/20 focus:border-cl-green focus:outline-none transition-colors resize-none"
            />
          </div>
          {state?.error ? (
            <p className="text-red-400 text-sm font-mono">{state.error}</p>
          ) : null}
          <SubmitButton />
        </form>
      </div>
    </div>
  );
}
